import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { config, featureEnabled } from "@/config";
import { MoneyFeatures } from "@/config/constants";
import { routeNames } from "@/router/constants";
import { useApolloClient } from "@vue/apollo-composable";
import { customerQuery } from "@/api/userApi";
import useAuthUser, { type AuthUser } from "@/composables/useAuthUser";
import { rawQueryParam } from "@/utils/url";
import * as Sentry from "@sentry/vue";
import { useBankingContextStore } from "@/stores/bankingContext";
import { BankingContextType } from "@/types/bankingContext";

export type NavigationGuardNext = RouteLocationRaw | undefined;

function queryStringParam(to: RouteLocationNormalized, key: string): string | undefined {
  const value = to.query[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// Invite emails use `/?signin=true&businessId=…&email=…`. Keep those on the auth redirect so the
// sign-in form can prefill (and so businessId survives for post-login resume).
function authRedirectQuery(to: RouteLocationNormalized): Record<string, string> | undefined {
  const query: Record<string, string> = {};
  if (!routeIsRoot(to)) {
    query.redirect_to = to.fullPath;
  }
  // Read off the raw query string, not to.query - vue-router decodes `+` as a space, which
  // corrupts an email using plus-addressing (see rawQueryParam).
  const email = rawQueryParam(to.fullPath, "email");
  const businessId = queryStringParam(to, "businessId");
  const signin = queryStringParam(to, "signin");
  if (email) {
    query.email = email;
  }
  if (businessId) {
    query.businessId = businessId;
  }
  if (signin) {
    query.signin = signin;
  }
  return Object.keys(query).length > 0 ? query : undefined;
}

/**
 * Route guard to check if the auth user is authenticated.
 * If the user is not authenticated, redirect them based on the enabled features.
 * If null, no redirect is necessary;
 */
export async function authRouteGuard(
  to: RouteLocationNormalized,
  { forceAuth = false } = {},
): Promise<{ goto?: NavigationGuardNext; authUser?: AuthUser } | undefined> {
  const { retrieveCurrentUser } = useAuthUser();
  const authUser = await retrieveCurrentUser();

  // If the user exists, proceed with the guard checks
  if (authUser) {
    return { authUser };
  }

  // If the route does not require authentication, allow navigation
  if (!to.matched.some(record => record.meta.requiresAuth ?? true) && !forceAuth) {
    return;
  }

  console.log("User not authenticated, redirecting to appropriate sign in view");

  const query = authRedirectQuery(to);

  if (featureEnabled(MoneyFeatures.InternetBanking)) {
    return { goto: { name: routeNames.SIGN_IN_VIEW, query } };
  } else if (featureEnabled(MoneyFeatures.Lending)) {
    return { goto: { name: routeNames.LOAN_APPLICATION_SPLASH, query } };
  } else if (featureEnabled(MoneyFeatures.Onboarding)) {
    return { goto: { name: routeNames.ONBOARDING_SPLASH, query } };
  } else {
    return { goto: { name: routeNames.SIGN_IN_VIEW, query } };
  }
}

/**
 * Route guard to check that the customer can be retrieved.
 * If the user can't be retrieved, the customer error view should be shown.
 * If null, no redirect is necessary;
 */
export async function customerRouteGuard(to: RouteLocationNormalized): Promise<NavigationGuardNext> {
  try {
    if (to.name === routeNames.CUSTOMER_ERROR_VIEW) {
      return;
    }

    const { resolveClient } = useApolloClient();
    const client = resolveClient();
    await client.query({ query: customerQuery, fetchPolicy: "cache-first" });
  } catch (error) {
    Sentry.captureException(error);
    console.error("Could not retrieve customer, redirecting to customer error view.");
    return { name: routeNames.CUSTOMER_ERROR_VIEW };
  }
}

/**
 * Route guard to check that the customer has the right role.
 * If the user does not have the correct role, the page-not-found view should be shown.
 * If null, no redirect is necessary;
 */
export async function roleRouteGuard(to: RouteLocationNormalized): Promise<NavigationGuardNext> {
  try {
    if (to.meta.requiresInternalUser) {
      const { isInternalUser } = useAuthUser();
      if (!isInternalUser.value) {
        console.warn("route requires internal user", to);
        return { name: routeNames.PAGE_NOT_FOUND };
      }
    }
    if (to.meta.requiresBroker) {
      const { isBroker } = useAuthUser();
      if (!isBroker.value) {
        console.warn("route requires broker user", to);
        return { name: routeNames.PAGE_NOT_FOUND };
      }
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("Could not evaluate auth user, redirecting to page not found view.", to);
    return { name: routeNames.PAGE_NOT_FOUND };
  }
}

/**
 * Settings routes marked requiresBusinessBankingContext are only valid in a business banking
 * context, and Authorised Users additionally requires ADMIN on that business.
 * Runs after auth so the banking-context store can rehydrate from sessionStorage.
 *
 * Personal-context or non-admin users land on Internet Banking Settings. Only apply this meta to
 * settings pages for now - a future business-only feature (e.g. batch payments) should not reuse
 * this redirect destination.
 */
export function businessBankingContextRouteGuard(to: RouteLocationNormalized): NavigationGuardNext {
  if (!to.meta.requiresBusinessBankingContext) {
    return;
  }
  const { context, loadingBusinesses, isActiveBusinessAdmin } = useBankingContextStore();
  if (context.type !== BankingContextType.Business) {
    return { name: routeNames.INTERNET_BANKING_SETTINGS };
  }
  // While businesses (and myRole) are still loading, let the settings view show its loading state.
  // Once loaded, only ADMINs may stay on Authorised Users.
  if (!loadingBusinesses && !isActiveBusinessAdmin) {
    return { name: routeNames.INTERNET_BANKING_SETTINGS };
  }
}

export async function loanRouteGuard(to: RouteLocationNormalized): Promise<NavigationGuardNext> {
  if (
    // If passwordless is disabled, the loan onboarding flow won't work
    !config.passwordless ||
    // Only require auth to view an existing loan application
    to.params.applicationId
  ) {
    const authNavigation = await authRouteGuard(to, { forceAuth: true });
    if (authNavigation?.goto) {
      console.log("loan route requires an authenticated user");
      return authNavigation?.goto;
    }
  }
}

function routeIsRoot(to: RouteLocationNormalized) {
  return to.path === "/";
}
